import { AnimatePresence, motion } from "motion/react";
import { useFormContext, useWatch } from "react-hook-form";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import { ControlledIntegerInput } from "@krea/shared/design-system/ControlledInputs/IntegerInput";
import { MoneyInput } from "@krea/shared/design-system/ControlledInputs/MoneyInput";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@krea/shared/design-system/Form";
import {
  RadioGroup,
  RadioGroupItem,
} from "@krea/shared/design-system/RadioGroup";
import type { Currency } from "@krea/shared/types/common";

import type { IFactoringBidInput } from "@/features/lender-responses/types/lender-response";
import type { BidPostData } from "@/validations";

import DetailsItem from "../../../../details/item";

function RegressRadioGroup({
  value,
  onChange,
  isSubmitting,
}: {
  value: string;
  onChange: (v: boolean) => void;
  isSubmitting: boolean;
}) {
  const { formItemId } = useFormField();
  const yesId = formItemId;
  const noId = `${formItemId}-no`;

  return (
    <RadioGroup
      onValueChange={(v) => onChange(v === "true")}
      value={value}
      disabled={isSubmitting}
      className="flex flex-row gap-4"
    >
      <FormItem className="flex items-center gap-2 space-y-0">
        <FormControl>
          <RadioGroupItem value="true" id={yesId} />
        </FormControl>
        <FormLabel htmlFor={yesId} className="cursor-pointer font-normal">
          Yes
        </FormLabel>
      </FormItem>
      <FormItem className="flex items-center gap-2 space-y-0">
        <FormControl>
          <RadioGroupItem value="false" id={noId} />
        </FormControl>
        <FormLabel htmlFor={noId} className="cursor-pointer font-normal">
          No
        </FormLabel>
      </FormItem>
    </RadioGroup>
  );
}

const withRegressInputVariants = {
  initial: {
    opacity: 0,
    x: "-100vw",
  },
  animate: {
    opacity: 1,
    x: 0,
  },
  exit: {
    opacity: 0,
    x: "-100vw",
    transition: {
      duration: 0.6,
    },
  },
};

interface LoanFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean; // Is editing existing offer or creating new
  isInterestRatePerYearActivated?: boolean;
}

export const FactoringForm = ({
  bidFormIndex,
  currency,
  isEditing,
}: LoanFormProps) => {
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<BidPostData<IFactoringBidInput>>();

  const bid = useWatch({
    control,
    name: `bids.${bidFormIndex}`,
  });

  const [formattedCurrencyValue, calculatedCurrency] = useFormatCurrency({
    currency,
  });

  const calculatedPurchasedAmount =
    ((bid.purchaseLimit ?? 0) * (bid.purchasedAmountPercent ?? 0)) / 100;

  return (
    <div className="flex w-full flex-col">
      <div className="w-full">
        <DetailsItem
          title={isEditing ? "Update factoring terms" : "Factoring terms"}
          className="mb-0"
        >
          <div className="w-full space-y-4">
            <MoneyInput
              name={`bids.${bidFormIndex}.purchaseLimit`}
              label="Purchase limit"
              control={control}
              currency={calculatedCurrency!}
              required
            />

            <ControlledIntegerInput
              name={`bids.${bidFormIndex}.purchasedAmountPercent`}
              label="Financing rate (in %)"
              control={control}
              required
              rightText={
                <>
                  <span className="mr-1">%</span>
                  <AnimatePresence>
                    {!!calculatedPurchasedAmount && (
                      <motion.span
                        className="inline-flex items-center gap-1.5"
                        initial={{
                          opacity: 0,
                          width: 0,
                        }}
                        animate={{
                          opacity: 1,
                          width: "auto",
                          transition: {
                            type: "tween",
                            duration: 0.1,
                            ease: "easeOut",
                          },
                        }}
                        exit={{
                          opacity: 0,
                          width: 0,
                          transition: {
                            type: "tween",
                            duration: 0.1,
                            ease: "easeOut",
                          },
                        }}
                      >
                        <span
                          aria-hidden
                          className="h-3 w-px shrink-0 bg-gray-400"
                        />
                        Calc:{" "}
                        {formattedCurrencyValue(calculatedPurchasedAmount)}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </>
              }
              placeholder="Value in %"
            />

            <ControlledIntegerInput
              name={`bids.${bidFormIndex}.commissionPercent`}
              label="Credit fee (per indicated number of 'Payment terms days' below)"
              control={control}
              required
              rightText="%"
            />
          </div>
        </DetailsItem>

        <DetailsItem className="mb-0">
          <div className="w-full">
            <ControlledIntegerInput
              name={`bids.${bidFormIndex}.paymentTermsDays`}
              label="Payment term days"
              control={control}
              acceptsDecimals={false}
              required
            />
          </div>
        </DetailsItem>

        <DetailsItem className="mb-0">
          <div className="w-full">
            <FormField
              control={control}
              name={`bids.${bidFormIndex}.withRegress`}
              render={({ field: { value, onChange } }) => {
                let radioValue = "";
                if (value === true) radioValue = "true";
                else if (value === false) radioValue = "false";

                return (
                  <FormItem>
                    <FormLabel className="font-bold">
                      Regress <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <RegressRadioGroup
                        value={radioValue}
                        onChange={onChange}
                        isSubmitting={isSubmitting}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
        </DetailsItem>

        <AnimatePresence mode="wait" initial={false}>
          {bid.withRegress && (
            <DetailsItem className="mb-0">
              <div className="w-full">
                <motion.div
                  key="withRegress"
                  initial="initial"
                  exit="exit"
                  animate="animate"
                  variants={withRegressInputVariants}
                >
                  <ControlledIntegerInput
                    name={`bids.${bidFormIndex}.regressNumDaysAfterDueDate`}
                    label="Regress number of days"
                    control={control}
                    required
                    rightText="days"
                    acceptsDecimals={false}
                  />
                </motion.div>
              </div>
            </DetailsItem>
          )}
        </AnimatePresence>

        <DetailsItem className="mb-0" title="">
          <div className="flex w-full flex-col gap-4">
            <MoneyInput
              name={`bids.${bidFormIndex}.setupFee`}
              label="Setup fee"
              control={control}
              currency={calculatedCurrency!}
            />
            <MoneyInput
              name={`bids.${bidFormIndex}.handlingFeePerInvoice`}
              label="Handling fee per invoice"
              control={control}
              currency={calculatedCurrency!}
            />
            <MoneyInput
              name={`bids.${bidFormIndex}.monthlyFee`}
              label="Monthly fee"
              control={control}
              currency={calculatedCurrency!}
            />
          </div>
        </DetailsItem>

        <DetailsItem className="mb-0" title="">
          <div className="w-full">
            <ControlledIntegerInput
              name={`bids.${bidFormIndex}.limitFeePerYearPercent`}
              label="Limit fee per year"
              control={control}
              rightText="%"
              required={false}
            />
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
